(() => {
  'use strict';
  const tidy = value => String(value || '').trim().replace(/\s+/g, ' ');
  const norm = value => tidy(value).toLowerCase();
  const storage = value => tidy(value).replace(/^(\d+)\s*(gb)?$/i, '$1GB').replace(/\s+(GB|TB)$/i, '$1').toUpperCase();
  const colour = value => /^(\(?product\)?\s*)?red$/i.test(tidy(value)) ? 'Red' : tidy(value);
  const grade = value => ({ a: 'A', excellent: 'A', b: 'B', good: 'B', c: 'C', fair: 'C' }[norm(value).replace(/^grade\s+/, '')] || tidy(value));
  const compare = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  function modelOf(data) {
    const title = tidy(data.title).replace(/^Apple\s+(?=iPhone|iPad|MacBook|iMac)/i, '');
    const tags = (data.tags || []).filter(tag => !/^(grade\b|\d+\s*(gb|tb)$|exact-device$)/i.test(tag));
    const model = tags.filter(tag => title.toLowerCase().startsWith(tag.toLowerCase())).sort((a, b) => b.length - a.length)[0];
    return model || title.split(/\s+\d+\s*(?:GB|TB)\b|\s+Refurbished\b|,/i)[0];
  }
  function familyOf(model) {
    const patterns = [/^(iPhone SE\s*\(?\d{4}\)?)/i, /^(iPhone\s+(?:\d+|X[RS]?))/i, /^(Samsung\s+Galaxy\s+[A-Z]\d+)/i, /^(?:Google\s+)?(Pixel\s+\d+)/i, /^(iPad(?:\s+(?:Pro|Air|mini))?)/i, /^(Apple Watch(?:\s+(?:Ultra|SE)\b)?)/i, /^(MacBook(?:\s+(?:Air|Pro))?)/i];
    for (const pattern of patterns) { const match = model.match(pattern); if (match) return match[1]; }
    return model;
  }
  function start(root) {
    if (!root.dataset.sortBound) {
      root.dataset.sortBound = 'true';
      const submit = root.querySelector('[data-sort-submit]');
      if (submit) submit.hidden = true;
      root.addEventListener('change', event => {
        const select = event.target.closest('[data-action="catalogue-sort"]');
        if (!select) return;
        const url = new URL(select.form.action, location.href);
        url.searchParams.set('sort_by', select.value);
        new URL(location.href).searchParams.forEach((value, key) => { if (key.startsWith('cd_') || key === 'preview_theme_id') url.searchParams.set(key, value); });
        location.assign(url);
      });
    }
    const panel = root.querySelector('[data-device-filters]');
    if (!panel || panel.dataset.started) return;
    panel.dataset.started = 'true';
    const words = panel.querySelector('[data-filter-words]').dataset;
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency', currency:words.currency}).format(value / 100);
    const node = (tag, text, className) => { const el=document.createElement(tag); if(text!=null) el.textContent=text; if(className) el.className=className; return el; };
    const controls = [...panel.querySelectorAll('input,select')];
    const keys = ['family', ...controls.map(el => el.name)];
    const state = Object.fromEntries(keys.map(key => [key, '']));
    const fieldset = panel.querySelector('fieldset');
    const status = panel.querySelector('[data-load-status]');
    const retry = panel.querySelector('[data-retry]');
    const grids = [...root.querySelectorAll('[data-stock-grid]')];
    if (!grids.length || root.dataset.catalogueEmpty === 'true') return;
    const grid = grids[0];
    let records = [], ready = false;
    panel.hidden = false;
    const readState = () => { const params = new URL(location.href).searchParams; keys.forEach(key => state[key] = params.get('cd_' + key) || ''); };
    const saveState = () => {
      const url = new URL(location.href);
      keys.forEach(key => state[key] ? url.searchParams.set('cd_' + key, state[key]) : url.searchParams.delete('cd_' + key));
      url.searchParams.delete('page');
      history.replaceState(null, '', url);
    };
    const readCards = () => {
      records = [...grid.querySelectorAll('[data-catalogue-card]')].map(card => {
        const data = JSON.parse(card.dataset.catalogueCard);
        const model = modelOf(data);
        const facts = { grade: grade(data.grade || data.condition), storage: storage(data.storage), colour: colour(data.colour) };
        const options = (data.options || []).map(norm);
        const variants = data.variants.filter(v => v.available).map(v => {
          const value = name => (v.options || [])[options.indexOf(name)] || '';
          return { price: Number(v.price ?? data.price), grade: grade(value('grade') || value('condition') || facts.grade), storage: storage(value('storage') || value('capacity') || facts.storage), colour: colour(value('colour') || value('color') || facts.colour) };
        });
        const rawBattery = tidy(data.battery).replace(/%$/, '');
        const battery = rawBattery !== '' && Number.isFinite(Number(rawBattery)) && Number(rawBattery) >= 0 && Number(rawBattery) <= 100 ? Number(rawBattery) : null;
        const device = data.device || data.exact || /phone|tablet|ipad|watch|laptop|macbook|console|gaming|computer/i.test(data.type);
        const family = device ? familyOf(model) : words.accessories;
        const parts = data.parts === 'original' ? words.original : data.parts === 'quality' ? words.quality : '';
        const componentFacts = card.querySelector('[data-card-parts]')?.textContent || '';
        const search = norm([variants.length > 1 && !data.exact ? model : data.title, model, family, battery == null ? words.unknown : battery + '%', parts, componentFacts].join(' '));
        return { card, item: card.closest('li'), data, model, family, variants: variants.length ? variants : [facts], battery, device, search };
      });
    };
    const priceValid = () => ['price_min','price_max'].every(key => !state[key] || /^\d+(?:\.\d{1,2})?$/.test(state[key])) && (!state.price_min || !state.price_max || Number(state.price_min) <= Number(state.price_max));
    const selectedVariants = record => record.variants.filter(v => {
      if (!priceValid()) return false;
      if (state.price_min && v.price < Math.round(Number(state.price_min)*100)) return false;
      if (state.price_max && v.price > Math.round(Number(state.price_max)*100)) return false;
      if (!['grade', 'storage', 'colour'].every(key => !state[key] || norm(v[key]) === norm(state[key]))) return false;
      const label = { A: words.excellent, B: words.good, C: words.fair }[v.grade] || v.grade;
      const search = record.search + ' ' + norm([label, v.grade ? 'grade ' + v.grade : '', v.storage, v.colour].join(' '));
      return norm(state.search).split(' ').filter(Boolean).every(term => search.includes(term));
    });
    const matches = (record, skipFamily = false) => {
      if (!skipFamily && state.family && record.family !== state.family) return false;
      if (!skipFamily && state.model && record.model !== state.model) return false;
      if (state.parts && record.data.parts !== state.parts) return false;
      if (!selectedVariants(record).length) return false;
      if (state.battery && (!record.device || (state.battery === 'unknown' ? record.battery !== null : record.battery === null || record.battery < Number(state.battery)))) return false;
      return true;
    };
    let familiesExpanded = false;
    function showFamilies() {
      const buttons = [...panel.querySelectorAll('[data-family]')];
      buttons.forEach((button, index) => { button.hidden = !familiesExpanded && index >= 6 && button.dataset.family !== state.family; });
      const more = panel.querySelector('[data-action="catalogue-families-more"]');
      more.hidden = buttons.length <= 6;
      more.setAttribute('aria-expanded', String(familiesExpanded));
      more.textContent = familiesExpanded ? words.fewerFamilies : words.showFamilies;
    }
    function modelOptions() {
      const select = panel.querySelector('[name=model]');
      select.length = 1;
      const values = [...new Set(records.filter(r => r.device && (!state.family || r.family === state.family)).map(r => r.model))].sort(compare);
      values.forEach(value => option(select, value));
      if (state.model && !values.includes(state.model)) option(select, state.model);
      select.value = state.model;
    }
    function apply(write = true) {
      if (!ready) return;
      controls.forEach(el => { if (el.value !== state[el.name]) el.value = state[el.name]; });
      let shown = 0;
      records.forEach(record => {
        const hit = matches(record); record.item.hidden = !hit; if (hit) shown++;
        if (hit && record.variants.length > 1) {
          const prices=selectedVariants(record).map(v=>v.price), min=Math.min(...prices);
          record.card.querySelector('.cdcol__card-price').textContent = prices.some(p=>p!==min) ? words.priceFrom.replace('{price}',money(min)) : money(min);
          const monthly=record.card.querySelector('.cdcol__card-klarna strong');if(monthly) monthly.textContent=money(Math.floor(min/3))+'/mo';
        }
      });
      const sort=new URL(location.href).searchParams.get('sort_by');
      if(sort==='price-ascending'||sort==='price-descending') {
        const price=r=>Math.min(...selectedVariants(r).map(v=>v.price));
        [...records].sort((a,b)=>(price(a)-price(b))*(sort==='price-descending'?-1:1)).forEach(r=>grid.append(r.item));
      }
      panel.querySelector('[data-results]').textContent = words.results.replace('{shown}', shown).replace('{total}', records.length);
      panel.querySelector('[data-no-results]').hidden = shown !== 0;
      panel.querySelectorAll('[data-family]').forEach(button => {
        const family = button.dataset.family;
        const count = records.filter(r => (!family || r.family === family) && matches(r, true)).length;
        button.querySelector('.cdf__family-count').textContent = words.familyStock.replace('{count}', count);
        button.classList.toggle('cdf__family--empty', count === 0);
        button.setAttribute('aria-pressed', String(state.family === family));
      });
      showFamilies();
      const active = keys.filter(key => state[key]).length;
      panel.querySelector('[data-search-active]').textContent = state.search ? '(' + state.search + ')' : '';
      panel.querySelector('[data-filter-total]').textContent = active ? '(' + active + ')' : '';
      root.querySelectorAll('.cdcol__sold').forEach(el => el.hidden = active > 0);
      renderActive(shown);
      if (write) saveState();
    }
    const option = (select, value, text = value) => select.add(new Option(text, value));
    function populate() {
      for (const key of ['grade', 'storage', 'colour']) {
        const select = panel.querySelector('[name=' + key + ']');
        select.length = 1;
        const values = [...new Set(records.filter(r => r.device).flatMap(r => key === 'model' ? [r.model] : r.variants.map(v => v[key])).filter(Boolean))].sort(compare);
        for (const value of values) option(select, value, key === 'grade' ? ({ A: words.excellent, B: words.good, C: words.fair }[value] || value) : value);
        if (state[key] && !values.includes(state[key])) option(select, state[key]);
      }
      const partsSelect = panel.querySelector('[name=parts]'); partsSelect.length = 1;
      ['original', 'quality'].filter(value => records.some(r => r.data.parts === value)).forEach(value => option(partsSelect, value, words[value]));
      if (state.parts && ![...partsSelect.options].some(o => o.value === state.parts)) option(partsSelect, state.parts, words[state.parts] || state.parts);
      const batterySelect = panel.querySelector('[name=battery]'); batterySelect.length = 1;
      [80, 85, 90, 95, 100].filter(min => records.some(r => r.device && r.battery !== null && r.battery >= min)).forEach(min => option(batterySelect, String(min), min + '%+'));
      if (records.some(r => r.device && r.battery === null)) option(batterySelect, 'unknown', words.unknown);
      if (state.battery && ![...batterySelect.options].some(o => o.value === state.battery)) option(batterySelect, state.battery, state.battery === 'unknown' ? words.unknown : state.battery + '%+');
      const families = panel.querySelector('.cdf__families'); families.replaceChildren();
      const values = [...new Set(records.map(r => r.family))].sort((a, b) => a === words.accessories ? 1 : b === words.accessories ? -1 : compare(a, b));
      if (state.family && !values.includes(state.family)) values.push(state.family);
      ['', ...values].forEach(family => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.family = family; button.dataset.action = 'catalogue-family';
        const name = document.createElement('span'); name.className = 'cdf__family-name'; name.textContent = family || words.all;
        const count = document.createElement('span'); count.className = 'cdf__family-count';
        button.append(name, count); families.append(button);
      });
      modelOptions();
    }
    async function load() {
      ready = false; fieldset.disabled = true; retry.hidden = true; status.textContent = words.loading;
      try {
        const pages = Number(root.dataset.cataloguePages) || 1;
        const current = Number(root.dataset.cataloguePage) || 1;
        [...grid.children].forEach((el, i) => { if (!el.dataset.catalogueOrder) el.dataset.catalogueOrder = current * 1000 + i; });
        const seen = new Set([...grid.querySelectorAll('[data-product-id]')].map(el => el.dataset.productId));
        for (let page = 1; page <= pages; page++) {
          if (page === current) continue;
          const url = new URL(location.href); url.searchParams.set('page', page);
          const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('Collection unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = doc.querySelector('[data-catalogue-pages]');
          if (!nextRoot || Number(nextRoot.dataset.cataloguePage) !== page) throw new Error('Unexpected collection page');
          nextRoot.querySelectorAll('[data-stock-grid] [data-catalogue-card]').forEach((card, i) => {
            if (!seen.has(card.dataset.productId)) { seen.add(card.dataset.productId); const item = document.importNode(card.closest('li'), true); item.dataset.catalogueOrder = page * 1000 + i; grid.append(item); }
          });
        }
        readCards();
        records.sort((a, b) => Number(a.item.dataset.catalogueOrder) - Number(b.item.dataset.catalogueOrder));
        const sort = new URL(location.href).searchParams.get('sort_by');
        if (sort === 'price-ascending' || sort === 'price-descending') records.sort((a, b) => (a.data.price - b.data.price) * (sort === 'price-descending' ? -1 : 1));
        else if (sort === 'title-ascending') records.sort((a, b) => compare(a.data.title, b.data.title));
        records.forEach(r => grid.append(r.item));
        root.querySelectorAll('.cdcol__pagination').forEach(el => el.hidden = true);
        if (records.length) root.querySelectorAll('.cdcol__none-now,[data-stock-fallback]').forEach(el => el.hidden = true);
        else panel.hidden = true;
        populate(); ready = true; fieldset.disabled = false; status.textContent = ''; apply(false); setupCompare();
      } catch (_) { status.textContent = words.error; retry.hidden = false; }
    }

    function filterLabel(key) {
      const el=controls.find(c=>c.name===key);
      if (key==='family') return state[key];
      if (key==='price_min'||key==='price_max') return (key==='price_min'?words.priceMin:words.priceMax).replace(' (£)','')+': '+money(Number(state[key])*100);
      const label=el.closest('label').firstChild.textContent.trim();
      return label+': '+(el.tagName==='SELECT' ? el.selectedOptions[0]?.textContent || state[key] : state[key]);
    }
    function removeFilter(key) {
      state[key]=''; if(key==='family') state.model=''; modelOptions(); apply();
      const next=panel.querySelector('[data-remove-filter]') || panel.querySelector('[data-clear]'); next.focus();
    }
    function renderActive(shown) {
      const active=panel.querySelector('[data-active-filters]'),suggestions=panel.querySelector('[data-filter-suggestions]');
      active.replaceChildren(); suggestions.replaceChildren();
      active.hidden=!keys.some(k=>state[k]);
      panel.querySelector('[data-price-error]').hidden=priceValid();
      for(const key of keys.filter(k=>state[k])) {
        const label=filterLabel(key),button=node('button',label+' ×');button.type='button';button.dataset.action='catalogue-remove-filter';button.dataset.removeFilter=key;
        button.setAttribute('aria-label',words.removeFilter.replace('{filter}',label));active.append(button);
        if(shown===0) {
          const previous={...state}; state[key]='';if(key==='family')state.model='';const count=records.filter(r=>matches(r)).length;Object.assign(state,previous);
          if(count) {const suggestion=node('button',words.withoutFilter.replace('{filter}',label).replace('{count}',count));suggestion.type='button';suggestion.dataset.action='catalogue-remove-filter';suggestion.dataset.removeFilter=key;suggestions.append(suggestion);}
        }
      }
    }
    let compared=[];
    const dialog=panel.querySelector('[data-compare-dialog]');
    const saveCompare=()=>{const url=new URL(location.href);if(compared.length)url.searchParams.set('cd_compare',compared.join(','));else url.searchParams.delete('cd_compare');history.replaceState(null,'',url);};
    function restoreCompare() {
      compared=[...new Set((new URL(location.href).searchParams.get('cd_compare')||'').split(','))].filter(id=>records.some(r=>r.device&&r.card.dataset.productId===id)).slice(0,3);updateCompare();
    }
    function setupCompare() {
      for(const record of records.filter(r=>r.device)) {
        if(record.item.querySelector('[data-compare-id]'))continue;
        const button=node('button',words.compare,'cdc-compare-toggle');button.type='button';button.dataset.action='catalogue-compare-toggle';button.dataset.compareId=record.card.dataset.productId;button.setAttribute('aria-label',words.compare+' '+record.data.title);record.item.append(button);
      }
      restoreCompare();
    }
    function updateCompare() {
      const selected=records.filter(r=>compared.includes(r.card.dataset.productId));
      root.querySelectorAll('.cdc-compare-toggle').forEach(button=>{const on=compared.includes(button.dataset.compareId);button.setAttribute('aria-pressed',String(on));button.textContent=on?'✓ '+words.compareSelected:words.compare;button.disabled=!on&&compared.length>=3;});
      const bar=panel.querySelector('[data-compare-bar]');bar.hidden=!selected.length;
      bar.querySelector('[data-compare-summary]').textContent=words.compareCount.replace('{count}',selected.length);
      bar.querySelector('[data-compare-guidance]').textContent=selected.length===3?words.compareLimit:words.compareHelp;
      bar.querySelector('[data-action="catalogue-compare-open"]').disabled=selected.length<2;
      const list=bar.querySelector('[data-compare-selection]');list.replaceChildren();
      selected.forEach(r=>{const button=node('button',r.model+' ×');button.type='button';button.dataset.action='catalogue-compare-toggle';button.dataset.compareId=r.card.dataset.productId;button.setAttribute('aria-label',words.compareRemove.replace('{device}',r.data.title));list.append(button);});
      if(dialog.open) {if(selected.length<2){dialog.close();bar.querySelector('[data-compare-selection] button')?.focus();}else renderComparison();}
    }
    function renderComparison() {
      const selected=compared.map(id=>records.find(r=>r.card.dataset.productId===id)).filter(Boolean),table=node('table',null,'cdc-compare-table'),head=node('thead'),headrow=node('tr');
      const feature=node('th',words.compareFeature);feature.scope='col';headrow.append(feature);
      selected.forEach(r=>{const th=node('th');th.scope='col';const img=r.card.querySelector('img');if(img){const photo=img.cloneNode();photo.alt='';th.append(photo);}const link=node('a',r.model);link.href=r.card.getAttribute('href');th.append(link);const remove=node('button','×');remove.type='button';remove.dataset.action='catalogue-compare-toggle';remove.dataset.compareId=r.card.dataset.productId;remove.setAttribute('aria-label',words.compareRemove.replace('{device}',r.data.title));th.append(remove);headrow.append(th);});
      head.append(headrow);table.append(head);const body=node('tbody');
      const choices=(r,key)=>[...new Set(r.variants.map(v=>key==='grade'?({A:words.excellent,B:words.good,C:words.fair}[v.grade]||v.grade):v[key]).filter(Boolean))].join(' / ')||words.unknown;
      const part=(r,index)=>{const rows=[...r.card.querySelectorAll('.cdc-part')];const row=rows.find(row=>norm(row.querySelector('dt')?.textContent)===norm(index===0?words.partBattery:words.partScreen));return row?[...row.querySelector('dd').children].map(el=>tidy(el.textContent)).filter(Boolean).join(' · '):words.unknown;};
      const rows=[
        [words.price,r=>{const prices=r.variants.map(v=>v.price),min=Math.min(...prices);return prices.some(p=>p!==min)?words.priceFrom.replace('{price}',money(min)):money(min);}],
        [words.grade,r=>choices(r,'grade')],[words.storage,r=>choices(r,'storage')],[words.colour,r=>choices(r,'colour')],
        [words.battery,r=>r.battery==null?words.unknown:r.battery+'%'],[words.cycles,r=>/^\d+$/.test(r.data.cycles)?r.data.cycles:words.unknown],
        [words.partBattery,r=>part(r,0)],[words.partScreen,r=>part(r,1)],
        [words.warranty,r=>r.data.warranty?words.warrantyMonths.replace('{count}',r.data.warranty):words.unknown]
      ];
      rows.forEach(([label,value])=>{const tr=node('tr'),th=node('th',label);th.scope='row';tr.append(th);selected.forEach(r=>tr.append(node('td',value(r))));body.append(tr);});
      const links=node('tr');links.append(node('th',''));selected.forEach(r=>{const td=node('td'),a=node('a',words.compareView);a.href=r.card.getAttribute('href');td.append(a);links.append(td);});body.append(links);table.append(body);panel.querySelector('[data-compare-table]').replaceChildren(table);
    }
    root.addEventListener('click',event=>{
      const button=event.target.closest('button[data-action]');if(!button)return;
      const action=button.dataset.action;
      if(action==='catalogue-remove-filter')removeFilter(button.dataset.removeFilter);
      if(action==='catalogue-compare-toggle') {const id=button.dataset.compareId;if(compared.includes(id))compared=compared.filter(x=>x!==id);else if(compared.length<3)compared.push(id);updateCompare();saveCompare();if(dialog.open)dialog.querySelector('[data-action="catalogue-compare-close"]').focus();}
      if(action==='catalogue-compare-clear'){compared=[];updateCompare();saveCompare();root.querySelector('.cdc-compare-toggle')?.focus();}
      if(action==='catalogue-compare-open'&&compared.length>=2){renderComparison();dialog.showModal();}
      if(action==='catalogue-compare-close')dialog.close();
    });

    readState();
    const revealSearch = () => { if (state.search) panel.querySelector('.cdf__search-panel').open = true; };
    revealSearch();
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape' && event.target.matches('[name=search]')) {
        const searchPanel = panel.querySelector('.cdf__search-panel');
        searchPanel.open = false;
        searchPanel.querySelector('summary').focus();
      }
    });
    const filter = event => {
      const el = event.target.closest('[data-action="catalogue-filter"]');
      if (!el || (event.type === 'input') !== el.matches('input')) return;
      state[el.name] = el.value; apply();
    };
    panel.addEventListener('input', filter);
    panel.addEventListener('change', filter);
    panel.addEventListener('click', event => {
      const button = event.target.closest('button[data-action]');
      if (!button) return;
      if (button.dataset.action === 'catalogue-family') { state.family = button.dataset.family; state.model = ''; modelOptions(); apply(); }
      if (button.dataset.action === 'catalogue-families-more') { familiesExpanded = !familiesExpanded; showFamilies(); }
      if (button.dataset.action === 'catalogue-clear') { keys.forEach(key => state[key] = ''); modelOptions(); apply(); }
      if (button.dataset.action === 'catalogue-retry') load();
    });
    window.addEventListener('popstate', () => { readState(); revealSearch(); populate(); apply(false); restoreCompare(); });
    load();
  }
  document.querySelectorAll('[data-catalogue-pages]').forEach(start);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-catalogue-pages]').forEach(start));
})();
